import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup, MatTab } from '@angular/material/tabs';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  DialogService,
  DIALOG_RETURN_TYPES,
  DialogComponent,
} from '@next/core-lib/dialog';
import type { AbstractDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Calculation, GenderOptions } from 'src/app/feed-profit/models/feed-profit.model';
import { getIsChanged } from 'src/app/feed-profit/store/feed-profit.selectors';

import { ProductsComponent } from './products/products.component';
import * as actions from '../../../store/feed-profit.actions';
import { BaselineSettingsComponent } from '../../shared/baseline-settings/baseline-settings.component';
import { ExtendedBaselineSettingsComponent } from '../../shared/extended-baseline-settings/extended-baseline-settings.component';

export interface ExtendedSimulationDialogInputData extends AbstractDialogData {
  calculation: Calculation;
}
export interface ExtendedSimulationDialogResultData {
  calculation: Calculation;
}

@Component({
  selector: 'app-extended-simulation-item',
  templateUrl: './extended-simulation-item.component.html',
  styleUrls: ['./extended-simulation-item.component.scss'],
  imports: [
    DialogComponent,
    MatTabGroup,
    MatTab,
    ProductsComponent,
    BaselineSettingsComponent,
    ExtendedBaselineSettingsComponent,
    TranslatePipe,
  ],
})
export class ExtendedSimulationItemComponent
  extends AbstractDialog<ExtendedSimulationDialogResultData>
  implements OnInit, OnDestroy
{
  calculation: Calculation;

  genders: GenderOptions;
  @ViewChild(ProductsComponent) productsComponent: ProductsComponent;
  @ViewChild(BaselineSettingsComponent) baselineSettingsComponent: BaselineSettingsComponent;
  @ViewChild(ExtendedBaselineSettingsComponent)
  extendedBaselineSettingsComponent: ExtendedBaselineSettingsComponent;

  calculationStateSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExtendedSimulationDialogInputData,
    protected mdDialogRef: MatDialogRef<
      ExtendedSimulationItemComponent,
      AbstractDialogResultData<ExtendedSimulationDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {
    super();
  }
  ngOnDestroy(): void {
    if (this.calculationStateSubscription) {
      this.calculationStateSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.calculation = this.data.calculation;
    this.genders = this.calculation?.genders;

    //Take initial value from the calculation in the case of a new Extended Simulation
    if (
      this.data.calculation &&
      !this.data.calculation.extendedSimulation?.baseLine &&
      !this.data.calculation.extendedSimulation?.extendedBaseLine
    ) {
      this.calculation.extendedSimulation.country = this.data.calculation.country;
      this.calculation.extendedSimulation.genders = this.data.calculation.genders;

      this.calculation.extendedSimulation.baseLine = this.data.calculation.baseLine;
      this.calculation.extendedSimulation.extendedBaseLine = {
        ...this.calculation.extendedBaseLine,
        feedPackagePrice: null,
      };
      this.calculation.extendedSimulation.feeders = this.data.calculation.feeders;
      this.calculation.extendedSimulation.calculatedFeeder = this.data.calculation.calculatedFeeder;

      this.store.dispatch(actions.setIsChanged({ isChanged: true }));
    }
  }

  save() {
    this.calculation = {
      ...this.calculation,
      extendedSimulation: {
        ...this.calculation.extendedSimulation,
        calculatedFeeder: this.calculation.extendedSimulation?.calculatedFeeder,
        feeders: this.calculation.extendedSimulation?.feeders,
      },
    };

    if (
      this.extendedBaselineSettingsComponent.form.valid &&
      this.baselineSettingsComponent.form.valid
    ) {
      const baseLineFeedersComponent = this.productsComponent.baselineFeedersComponent;

      // Send values to parent.
      const feeders = [
        ...(baseLineFeedersComponent?.startFeederRows || []),
        ...(baseLineFeedersComponent?.growthFeederRows || []),
        ...(baseLineFeedersComponent?.endFeederRows || []),
      ];
      this.store.dispatch(actions.setIsChanged({ isChanged: false }));

      this.confirm({
        calculation: {
          ...this.calculation,
          extendedSimulation: {
            feeders,
            ...this.calculation.extendedSimulation,
            baseLine: this.baselineSettingsComponent.getCalculation().extendedSimulation.baseLine,
            extendedBaseLine:
              this.extendedBaselineSettingsComponent.getCalculation().extendedSimulation
                .extendedBaseLine,
          },
        },
      });
    }
  }
  cancel() {
    this.store
      .select(getIsChanged)
      .pipe(take(1))
      .subscribe((isChanged) => {
        if (
          this.productsComponent.form.dirty ||
          this.baselineSettingsComponent.form.dirty ||
          this.extendedBaselineSettingsComponent.form.dirty ||
          isChanged
        ) {
          this.dialog
            .pendingChanges({
              title: this.localeService.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-title',
              ),
              subTitle: this.localeService.translate(
                'feed-profit.detail.extended-simulation.item-cancellation-subtitle',
              ),
              pushHistoryState: false,
            })
            .subscribe((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                this.mdDialogRef.close();
                this.store.dispatch(actions.setIsChanged({ isChanged: false }));
              } else {
                this.canceling = false;
                this.mdDialogRef.disableClose = true;
              }
            });
        } else {
          this.mdDialogRef.close();
        }
      });
  }

  calculationSettingsChanged($event: Calculation) {
    this.calculation = $event;
  }

  selectedTabChange($event: MatTabChangeEvent) {
    this.calculation = this.getCalculation();

    switch ($event.index) {
      case 0:
        this.productsComponent.baselineFeedersComponent.updateSubComponents(this.calculation);
        break;
      case 1:
        this.baselineSettingsComponent.setBaseLineSettingsValues(this.calculation);
        break;
      case 2:
        this.extendedBaselineSettingsComponent.setTableValues(this.calculation);
        break;
      default:
        break;
    }
  }

  getCalculation() {
    return {
      ...this.calculation,
      extendedSimulation: {
        ...this.calculation.extendedSimulation,
        baseLine: this.baselineSettingsComponent.getCalculation().extendedSimulation.baseLine,
        extendedBaseLine:
          this.extendedBaselineSettingsComponent.getCalculation().extendedSimulation
            .extendedBaseLine,
      },
    };
  }
}
